import React, { useRef, useState } from "react";
import { useTimeSheetStore } from "../../../../config/stores";
import { isEmpty } from "lodash";
import {
  Alert,
  Card,
  Col,
  Descriptions,
  notification,
  Popconfirm,
  Row,
  Space,
  Tag,
} from "antd";
import AppLoader from "../../../../components/common/AppLoader";
import dayjs from "dayjs";
import AppButton from "../../../../components/common/AppButton";
import {
  AiFillCheckCircle,
  AiFillCloseCircle,
  AiFillStop,
  AiOutlineArrowUp,
  AiOutlinePrinter,
  AiOutlineReload,
} from "react-icons/ai";
import TimeSheetActivitiesTable from "../TimeSheetActivities/TimeSheetActivitiesTable";
import TimeSheetActivitiesForm from "./TimeSheetActivitiesForm";
import TimeSheetDayActivities from "../TimeSheetActivities/DayActivities";
import { useReactToPrint } from "react-to-print";
import PrintTimeSheet from "./PrintTimeSheet";
import { rejectionsActions } from "../../../../config/actions/rejections/rejections.actions";
import { useDispatch } from "react-redux";

const STATUS_COLORS = {
  pending: "default",
  submitted: "processing",
  approved: "success",
  rejected: "error",
};

const TimeSheetDetails = () => {
  const timeSheetStore = useTimeSheetStore();
  const [showHeader, setShowHeader] = useState(false);

  const { timeSheetDetails, gettingTimeSheetDetails } = timeSheetStore;

  const closeTimeSheet = () =>
    timeSheetStore.setShowViewTimeSheetDetails(false);

  // month is stored as a plain number (1-12); build a proper date for formatting
  const monthDate = (timeSheetDetails?.year && timeSheetDetails?.month)
    ? dayjs(`${timeSheetDetails.year}-${String(timeSheetDetails.month).padStart(2, "0")}-01`)
    : dayjs();
  const month    = monthDate.format("MMMM");
  const firstDay = monthDate.startOf("month").format("DD");
  const lastDay  = monthDate.endOf("month").format("DD");

  const approvedAt = dayjs(timeSheetDetails.approved_at);

  const componentRef = useRef();

  const pageStyle = `
  @page {
    size: A4 landscape;
    margin: 8mm;
  }

  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    html, body {
      height: initial !important;
      overflow: initial !important;
    }
  }
 `;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    contentRef: componentRef,
    documentTitle: `${timeSheetDetails?.user?.first_name || ""} ${
      timeSheetDetails?.user?.other_names || ""
    } ${month} ${timeSheetDetails.year} TIME SHEET`,
    pageStyle,
    onAfterPrint: () => {
      setShowHeader(false);
    },
  });
  const onPrint = () => {
    setShowHeader(true);
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  const dispatch = useDispatch();

  const reject = () => {
    dispatch(
      rejectionsActions.toggleRejectionsModal({
        visible: true,
        data: {
          user_to_id: timeSheetDetails.created_by_id,
          item_application: "TIME SHEET",
          item_id: timeSheetDetails?.id,
          itemName: `${timeSheetDetails?.user?.first_name || ""} ${
            timeSheetDetails?.user?.other_names || ""
          } ${month} ${timeSheetDetails.year} TIME SHEET `,
        },
      })
    );
  };

  return (
    <div>
      {!isEmpty(timeSheetStore.getTimeSheetDetailsError) && (
        <Alert
          message={timeSheetStore.getTimeSheetDetailsError?.message}
          type="error"
          showIcon
          className="my-2"
          action={
            <Space>
              <AppButton
                text="Close"
                type="default"
                iconBefore={<AiFillCloseCircle />}
                danger
                onClick={closeTimeSheet}
              />
            </Space>
          }
        />
      )}
      {isEmpty(timeSheetDetails) &&
      !gettingTimeSheetDetails &&
      isEmpty(timeSheetStore.getTimeSheetDetailsError) ? (
        <AppLoader message="GETTING TIME SHEET DETAILS" />
      ) : (
        <Row>
          <Col span={24}>
            <Card
              title={`${month} ${timeSheetDetails.year} TIME SHEET`.toUpperCase()}
              extra={
                <Space>
                  {timeSheetDetails.status === "pending" && (
                    <AppButton
                      text="Save"
                      onClick={() =>
                        notification.success({
                          message: "Successfully saved time sheet",
                        })
                      }
                    />
                  )}
                  <Popconfirm
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{
                      danger: true,
                    }}
                    okButtonProps={{
                      color: "green",
                    }}
                    onConfirm={() =>
                      timeSheetStore.approveTimeSheet(timeSheetDetails.id)
                    }
                    title="Are you sure you want to approve this time sheet"
                  >
                    {timeSheetDetails.status !== "approved" &&
                      timeSheetDetails.status !== "pending" && (
                        <Space>
                          <AppButton
                            text="Approve"
                            type="submit"
                            loadingText="Approving..."
                            iconBefore={<AiFillCheckCircle />}
                            loading={timeSheetStore.approvingTimeSheet}
                          />
                        </Space>
                      )}
                  </Popconfirm>

                  <Popconfirm
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{
                      danger: true,
                    }}
                    okButtonProps={{
                      color: "green",
                    }}
                    onConfirm={() => reject()}
                    title="Are you sure you want to reject this time sheet"
                  >
                    {timeSheetDetails.status !== "approved" &&
                      timeSheetDetails.status !== "pending" && (
                        <Space>
                          <AppButton
                            text="Reject"
                            danger
                            iconBefore={<AiFillStop />}
                          />
                        </Space>
                      )}
                  </Popconfirm>
                  <Popconfirm
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{
                      danger: true,
                    }}
                    okButtonProps={{
                      color: "green",
                    }}
                    onConfirm={() =>
                      timeSheetStore.submitTimeSheet(timeSheetDetails.id)
                    }
                    title="Are you sure you want to submit this time sheet"
                  >
                    {timeSheetDetails.status !== "approved" &&
                      timeSheetDetails.status === "pending" && (
                        <AppButton
                          text="Submit"
                          type="submit"
                          loadingText="Submitting..."
                          iconBefore={<AiOutlineArrowUp />}
                          loading={timeSheetStore.submittingTimesSheet}
                        />
                      )}
                  </Popconfirm>
                  <AppButton
                    iconBefore={<AiOutlinePrinter />}
                    text="Download"
                    loading={showHeader}
                    loadingText="Downloading..."
                    onClick={onPrint}
                  />
                  <AppButton
                    text="Reload"
                    type="default"
                    iconBefore={<AiOutlineReload />}
                    onClick={() =>
                      timeSheetStore.getTimeSheetDetails(timeSheetDetails.id)
                    }
                  />
                  <AppButton
                    text="Close"
                    type="default"
                    iconBefore={<AiFillCloseCircle />}
                    danger
                    onClick={closeTimeSheet}
                  />
                </Space>
              }
            >
              <div className="d-none">
                <div ref={componentRef}>
                  <PrintTimeSheet />
                </div>
              </div>
              <div>
                <Descriptions
                  bordered
                  size="small"
                  className="mb-3"
                  column={{ xs: 1, sm: 1, md: 2, lg: 3 }}
                  labelStyle={{ fontWeight: 600, width: 140 }}
                >
                  <Descriptions.Item label="PERIOD">
                    {firstDay}-{lastDay} {month} {timeSheetDetails.year}
                  </Descriptions.Item>
                  <Descriptions.Item label="NAME OF STAFF">
                    {timeSheetDetails?.user?.first_name || ""}{" "}
                    {timeSheetDetails?.user?.other_names || ""}
                  </Descriptions.Item>
                  <Descriptions.Item label="POSITION">
                    {timeSheetDetails?.role?.role || ""}
                  </Descriptions.Item>
                  <Descriptions.Item label="STATUS">
                    <Tag
                      color={STATUS_COLORS[timeSheetDetails?.status] || "default"}
                      className="text-uppercase"
                    >
                      {timeSheetDetails?.status || ""}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="APPROVED BY">
                    {`${timeSheetDetails?.approved_by?.first_name || "NOT"} ${
                      timeSheetDetails?.approved_by?.other_names || " APPROVED"
                    }`}
                  </Descriptions.Item>
                  <Descriptions.Item label="APPROVED AT">
                    {approvedAt.isValid()
                      ? approvedAt.format("DD/MM/YYYY HH:mm")
                      : "NOT APPROVED"}
                  </Descriptions.Item>
                </Descriptions>
                <TimeSheetActivitiesTable showHeader={showHeader} />
              </div>
              <TimeSheetActivitiesForm />
              <TimeSheetDayActivities />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TimeSheetDetails;
